import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import api, { billingKontoAPI } from '@/services/api';
import { Eye, EyeOff, Building2, UserPlus, Check, Ticket, X, Loader2, ArrowLeft, ArrowRight, MapPin } from 'lucide-react';
import { formatISK, formatKennitala, parseKennitala } from '@/utils/formatISK';
import KennitalaInput from '@/components/KennitalaInput';
import { planTagline } from '@/utils/billingCopy';

const PLAN_ORDER = ['solo', 'team', 'business'];
const PLAN_BADGE = { team: 'Vinsælast' };

const ISK = (n) => formatISK(Math.round(n || 0));

const IS_MONTHS = ['janúar', 'febrúar', 'mars', 'apríl', 'maí', 'júní', 'júlí', 'ágúst', 'september', 'október', 'nóvember', 'desember'];
const EN_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// iter368: the date comes from the server as a plain YYYY-MM-DD (Reykjavik)
// and is formatted from its parts, so the notice, the trial bar and the
// Áskrift modal always name the same day whatever time zone the phone is in.
const firstInvoiceDateLabel = (plainDate, lang) => {
  const [y, m, d] = String(plainDate || '').slice(0, 10).split('-');
  const idx = Number(m) - 1;
  if (!y || !d || isNaN(idx) || idx < 0 || idx > 11) return '';
  const months = lang === 'is' ? IS_MONTHS : EN_MONTHS;
  return lang === 'is'
    ? `${Number(d)}. ${months[idx]} ${y}`
    : `${months[idx]} ${Number(d)}, ${y}`;
};

const Register = () => {
  const [searchParams] = useSearchParams();
  const planFromUrl = PLAN_ORDER.includes(searchParams.get('plan')) ? searchParams.get('plan') : null;
  // Teymið is the plan most companies pick, so it is preselected when the
  // visitor arrives without ?plan= (they can still change it on step 2).
  const DEFAULT_PLAN = 'team';

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    company_name: '',
    kennitala: '',
    address: '',
    zip: '',
    city: '',
    invite_code: '',
    plan: planFromUrl || DEFAULT_PLAN,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showInviteCode, setShowInviteCode] = useState(false);
  const [existingPasswordMode, setExistingPasswordMode] = useState(false);
  const [existingPassword, setExistingPassword] = useState('');
  const [codeStatus, setCodeStatus] = useState(null);
  const [codeChecking, setCodeChecking] = useState(false);
  const debounceRef = useRef(null);
  const { register } = useAuth();
  const { t, lang, switchLang } = useI18n();
  const navigate = useNavigate();

  // Step 1 — kennitala lookup confirmation state
  const [lookupResult, setLookupResult] = useState(null); // { name, address, postal_code, city } | null
  const [companyConfirmed, setCompanyConfirmed] = useState(false);
  const [showManualFields, setShowManualFields] = useState(false);

  // Step 2 — plan catalog (prices come from the backend, never hardcoded)
  const [plans, setPlans] = useState([]);
  const [firstInvoiceDate, setFirstInvoiceDate] = useState('');
  useEffect(() => {
    billingKontoAPI.plansCatalog().then((res) => {
      setPlans(res.data?.plans || []);
      setFirstInvoiceDate(res.data?.first_invoice_date || '');
    }).catch(() => {});
  }, []);

  // iter364/365: the pakki chosen here IS the choice, so the sentence under
  // the button states the price of the selected pakki and the first invoice
  // date. The SERVER writes the same sentence into the subscription row from
  // its own plan table, so this copy is display only and is never posted.
  const selectedPlan = plans.find((p) => p.plan === formData.plan);
  const whenLabel = firstInvoiceDateLabel(firstInvoiceDate, lang);
  const signupNoticeText = !whenLabel
    ? (lang === 'is' ? '14 dagar frítt, nema þú segir upp.' : '14 days free, unless you cancel.')
    : selectedPlan
    ? (lang === 'is'
        ? `14 dagar frítt. Fyrsti reikningur ${whenLabel}, ${formatISK(selectedPlan.monthly_isk_inc_vat)} kr á mánuði, nema þú segir upp.`
        : `14 days free. First invoice ${whenLabel}, ${formatISK(selectedPlan.monthly_isk_inc_vat)} kr per month, unless you cancel.`)
    : (lang === 'is'
        ? `14 dagar frítt. Fyrsti reikningur ${whenLabel}, nema þú segir upp.`
        : `14 days free. First invoice ${whenLabel}, unless you cancel.`);

  const validateCode = useCallback(async (code) => {
    const trimmed = code.trim();
    if (!trimmed) {
      setCodeStatus(null);
      setCodeChecking(false);
      return;
    }
    setCodeChecking(true);
    try {
      const res = await api.post('/auth/invite-codes/validate', { code: trimmed });
      setCodeStatus(res.data.valid === true);
    } catch {
      setCodeStatus(false);
    } finally {
      setCodeChecking(false);
    }
  }, []);

  const handleCodeChange = (value) => {
    setFormData(prev => ({ ...prev, invite_code: value }));
    setCodeStatus(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => validateCode(value), 400);
  };

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'kennitala') {
      setFormData(prev => ({ ...prev, kennitala: formatKennitala(value) }));
      return;
    }
    setFormData({ ...formData, [name]: value });
  };

  const handleKennitalaFetched = (data) => {
    setLookupResult(data);
    setCompanyConfirmed(false);
    setShowManualFields(false);
  };

  const confirmLookupCompany = () => {
    setFormData(prev => ({
      ...prev,
      company_name: lookupResult?.name || prev.company_name,
      address: lookupResult?.address || prev.address,
      zip: lookupResult?.postal_code || prev.zip,
      city: lookupResult?.city || prev.city,
    }));
    setCompanyConfirmed(true);
    // "Já, halda áfram" goes straight to step 2 — no second click.
    setError('');
    setStep(2);
  };

  const rejectLookupCompany = () => {
    setLookupResult(null);
    setCompanyConfirmed(false);
    setShowManualFields(true);
  };

  const canContinueToStep2 = parseKennitala(formData.kennitala).length === 10 && formData.company_name.trim().length > 0;

  const handleContinue = (e) => {
    e.preventDefault();
    if (!canContinueToStep2) return;
    setError('');
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // ── Existing-email branch ────────────────────────────────────────────
    if (existingPasswordMode) {
      if (!existingPassword) { setError(lang === 'is' ? 'Sláðu inn núverandi lykilorðið' : 'Enter your current password'); return; }
      setIsLoading(true);
      try {
        const result = await register({
          name: formData.name,
          email: formData.email,
          password: existingPassword,
          company_name: formData.company_name,
          kennitala: parseKennitala(formData.kennitala),
          existing_password: existingPassword,
          plan: formData.plan,
          interval: 'month',
          address: formData.address,
          zip: formData.zip,
          city: formData.city,
        });
        if (result?.added_to_existing_user) {
          navigate('/login', { state: { message: result.message || (lang === 'is' ? 'Fyrirtækið var bætt við. Innskráðu þig til að halda áfram.' : 'Company added. Please log in to continue.') } });
        } else {
          navigate('/dashboard');
        }
      } catch (err) {
        const msg = err.message || 'Failed';
        if (/incorrect_existing_password/i.test(msg)) setError(lang === 'is' ? 'Rangt lykilorð' : 'Wrong password');
        else setError(msg);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (!formData.plan) {
      setError(lang === 'is' ? 'Veldu pakka til að halda áfram' : 'Pick a plan to continue');
      return;
    }
    if (!agreedToTerms) {
      setError(t('register.error_terms'));
      return;
    }
    if (formData.password.length < 8) {
      setError(t('register.error_password_short'));
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        company_name: formData.company_name,
        kennitala: parseKennitala(formData.kennitala),
        plan: formData.plan,
        interval: 'month',
        address: formData.address,
        zip: formData.zip,
        city: formData.city,
      };
      if (formData.invite_code.trim()) {
        payload.invite_code = formData.invite_code.trim();
      }
      try { sessionStorage.setItem('rektra_just_registered', '1'); } catch { /* private mode */ }
      await register(payload);
      // Straight to the welcome screen: routing via /dashboard first painted
      // the real homepage for a frame before onboarding took over (iter366).
      navigate('/welcome', { replace: true });
    } catch (err) {
      try { sessionStorage.removeItem('rektra_just_registered'); } catch { /* private mode */ }
      if (err.code === 'email_exists_password_required') {
        setExistingPasswordMode(true);
        setError(null);
        setIsLoading(false);
        return;
      }
      const msg = err.message || 'Registration failed';
      if (msg.includes('Invalid invite code')) setError(t('register.invite_code_error_invalid'));
      else if (msg.includes('expired')) setError(t('register.invite_code_error_expired'));
      else if (msg.includes('already been used')) setError(t('register.invite_code_error_used'));
      else setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Branding (desktop) */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-background-elevated border-r border-border">
        <div className="flex flex-col justify-center px-16">
          <img src="/logos/logo-white.png" alt="REKTRA" className="h-24 w-auto object-contain mb-8" data-testid="register-logo-desktop" />
          <p className="text-foreground-secondary text-lg leading-relaxed max-w-md">{t('register.tagline')}</p>
          <div className="mt-12 space-y-4 text-sm text-foreground-muted">
            {[t('register.bullet_1'), t('register.bullet_2'), t('register.bullet_3')].map((text, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-1 h-1 rounded-full bg-accent" />
                <span>{text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-background overflow-y-auto">
        <div className="w-full max-w-sm py-6">
          {/* Language toggle */}
          <div className="flex justify-end mb-4">
            <div className="flex rounded-md overflow-hidden text-xs font-medium border border-border" data-testid="register-lang-toggle">
              <button onClick={() => switchLang('is')} className={`px-2.5 py-1.5 transition-colors ${lang === 'is' ? 'bg-accent text-background' : 'text-foreground-muted'}`}>IS</button>
              <button onClick={() => switchLang('en')} className={`px-2.5 py-1.5 transition-colors ${lang === 'en' ? 'bg-accent text-background' : 'text-foreground-muted'}`}>EN</button>
            </div>
          </div>

          <div className="lg:hidden mb-8 text-center">
            <img src="/logos/logo-white.png" alt="REKTRA" className="h-16 w-auto object-contain mx-auto" data-testid="register-logo-mobile" />
          </div>

          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3" data-testid="register-step-indicator">
              <div className={`h-1.5 flex-1 rounded-full ${step >= 1 ? 'bg-accent' : 'bg-border'}`} />
              <div className={`h-1.5 flex-1 rounded-full ${step >= 2 ? 'bg-accent' : 'bg-border'}`} />
            </div>
            <h2 className="text-2xl font-semibold mb-2">
              {step === 1
                ? (lang === 'is' ? 'Fyrirtækið þitt' : 'Your company')
                : (lang === 'is' ? 'Búðu til aðganginn þinn' : 'Create your account')}
            </h2>
            {step === 2 && formData.company_name && (
              <p className="text-sm font-medium flex items-center gap-1.5 mb-1" style={{ color: 'var(--accent, #22C55E)' }} data-testid="register-step-2-company">
                <Building2 size={13} />{formData.company_name}
              </p>
            )}
            <p className="text-foreground-secondary">
              {step === 1
                ? (lang === 'is' ? 'Sláðu inn kennitöluna, við sækjum restina.' : 'Enter the kennitala and we fetch the rest.')
                : (formData.company_name ? '' : t('register.subtitle'))}
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg text-error text-sm" data-testid="register-error">
              {error}
            </div>
          )}

          {/* ── STEP 1: Company ── */}
          {step === 1 && (
            <form onSubmit={handleContinue} className="space-y-5" data-testid="register-step-1">
              <div className="space-y-4 p-5 bg-card rounded-lg border border-border">
                <div className="flex items-center gap-2 text-sm font-medium text-foreground-secondary">
                  <Building2 size={16} />
                  {t('register.company_section')}
                </div>
                <KennitalaInput
                  value={formData.kennitala}
                  onChange={(v) => setFormData(prev => ({ ...prev, kennitala: v }))}
                  onFetched={handleKennitalaFetched}
                  onFailed={() => { setLookupResult(null); setShowManualFields(true); }}
                  placeholder={t('register.kennitala_ph')}
                  testIdPrefix="register-kennitala"
                />

                {lookupResult && !companyConfirmed && (
                  <div className="p-4 rounded-lg border border-accent/40 bg-accent/5 space-y-3" data-testid="register-company-confirm">
                    <p className="text-sm font-semibold">{lang === 'is' ? 'Er þetta fyrirtækið þitt?' : 'Is this your company?'}</p>
                    <div className="text-sm text-foreground-secondary space-y-0.5">
                      <p className="font-medium text-foreground" data-testid="register-company-confirm-name">{lookupResult.name}</p>
                      {(lookupResult.address || lookupResult.city) && (
                        <p className="flex items-center gap-1.5 text-foreground-muted" data-testid="register-company-confirm-address">
                          <MapPin size={12} />
                          {[lookupResult.address, lookupResult.postal_code, lookupResult.city].filter(Boolean).join(', ')}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button type="button" onClick={confirmLookupCompany} className="flex-1 btn btn-primary text-sm py-2" data-testid="register-company-confirm-yes">
                        <Check size={14} />{lang === 'is' ? 'Já, halda áfram' : 'Yes, continue'}
                      </button>
                      <button type="button" onClick={rejectLookupCompany} className="flex-1 text-sm py-2 rounded-lg border border-border hover:bg-card-hover transition-colors" data-testid="register-company-confirm-no">
                        {lang === 'is' ? 'Nei, breyta' : 'No, edit'}
                      </button>
                    </div>
                  </div>
                )}

                {companyConfirmed && (
                  <div className="p-3 rounded-lg border border-accent/30 bg-accent/5 flex items-start justify-between gap-3" data-testid="register-company-confirmed">
                    <div className="text-sm">
                      <p className="font-medium flex items-center gap-1.5"><Check size={13} className="text-accent" />{formData.company_name}</p>
                      {formData.address && <p className="text-xs text-foreground-muted mt-0.5">{[formData.address, formData.zip, formData.city].filter(Boolean).join(', ')}</p>}
                    </div>
                    <button type="button" onClick={() => { setCompanyConfirmed(false); setShowManualFields(true); }} className="text-xs text-accent hover:underline whitespace-nowrap" data-testid="register-company-edit">
                      {lang === 'is' ? 'Breyta' : 'Edit'}
                    </button>
                  </div>
                )}

                {(showManualFields || (!lookupResult && !companyConfirmed)) && !companyConfirmed && (
                  <div className="space-y-3" data-testid="register-company-manual-fields">
                    <input name="company_name" type="text" value={formData.company_name} onChange={handleChange} placeholder={t('register.company_name_ph')} required data-testid="register-company" />
                    {showManualFields && (
                      <>
                        <input name="address" type="text" value={formData.address} onChange={handleChange} placeholder={lang === 'is' ? 'Heimilisfang' : 'Address'} data-testid="register-address" />
                        <div className="grid grid-cols-2 gap-3">
                          <input name="zip" type="text" value={formData.zip} onChange={handleChange} placeholder={lang === 'is' ? 'Póstnúmer' : 'Zip'} data-testid="register-zip" />
                          <input name="city" type="text" value={formData.city} onChange={handleChange} placeholder={lang === 'is' ? 'Staður' : 'City'} data-testid="register-city" />
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              <button type="submit" disabled={!canContinueToStep2} className="w-full btn btn-primary btn-lg disabled:opacity-40" data-testid="register-step-1-continue">
                {lang === 'is' ? 'Halda áfram' : 'Continue'}<ArrowRight size={16} />
              </button>
            </form>
          )}

          {/* ── STEP 2: Account + plan ── */}
          {step === 2 && (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-step-2">
              {existingPasswordMode && (
                <div className="p-4 rounded-lg border border-accent/40 bg-accent/5 space-y-3" data-testid="register-existing-mode">
                  <div>
                    <p className="text-sm font-semibold" data-testid="register-existing-title">
                      {lang === 'is' ? 'Þú átt þegar aðgang með þessu netfangi' : 'You already have an account with this email'}
                    </p>
                    <p className="text-xs text-foreground-muted mt-1" data-testid="register-existing-body">
                      {lang === 'is'
                        ? `Sláðu inn lykilorðið þitt til að bæta nýja fyrirtækinu (${formData.company_name || formData.kennitala}) við aðganginn þinn.`
                        : `Enter your password to add the new company (${formData.company_name || formData.kennitala}) to your account.`}
                    </p>
                  </div>
                  <input
                    type="password"
                    placeholder={lang === 'is' ? 'Núverandi lykilorð' : 'Current password'}
                    value={existingPassword}
                    onChange={(e) => setExistingPassword(e.target.value)}
                    autoFocus
                    required
                    data-testid="register-existing-password"
                  />
                  <div className="flex items-center justify-between gap-3">
                    <button
                      type="button"
                      onClick={() => { setExistingPasswordMode(false); setExistingPassword(''); setError(null); }}
                      className="text-xs text-foreground-muted hover:text-foreground underline"
                      data-testid="register-existing-cancel"
                    >
                      {lang === 'is' ? 'Hætta við og fara til baka' : 'Cancel and go back'}
                    </button>
                    <Link
                      to="/forgot-password"
                      className="text-xs text-accent hover:underline"
                      data-testid="register-existing-forgot"
                    >
                      {lang === 'is' ? 'Gleymt lykilorð?' : 'Forgot password?'}
                    </Link>
                  </div>
                </div>
              )}

              {!existingPasswordMode && (
                <>
                  <div className="space-y-4">
                    <input name="name" type="text" value={formData.name} onChange={handleChange} placeholder={t('register.name_ph')} required data-testid="register-name" />
                    <input name="email" type="email" value={formData.email} onChange={handleChange} placeholder={t('register.email_ph')} required data-testid="register-email" />
                    <div className="relative">
                      <input name="password" type={showPassword ? 'text' : 'password'} value={formData.password} onChange={handleChange} placeholder={t('register.password_ph')} required minLength={8} className="pr-11" data-testid="register-password" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-muted hover:text-foreground transition-colors">
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  {/* Plan picker — required, prices from backend catalog */}
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-foreground-secondary">
                      {lang === 'is' ? 'Veldu pakka' : 'Pick a plan'}
                      <span className="text-error ml-1">*</span>
                    </label>
                    <div className="space-y-2" data-testid="register-plan-picker">
                      {plans.map((p) => {
                        const selected = formData.plan === p.plan;
                        return (
                          <button
                            key={p.plan}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, plan: p.plan }))}
                            className={`w-full flex items-start justify-between gap-3 px-4 py-3 rounded-lg border text-left transition-colors ${selected ? 'border-accent bg-accent/10' : 'border-border hover:bg-card-hover'}`}
                            data-testid={`register-plan-${p.plan}`}
                          >
                            <span className="flex items-start gap-2 min-w-0">
                              <span className={`w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selected ? 'border-accent' : 'border-foreground-muted'}`}>
                                {selected && <span className="w-2 h-2 rounded-full bg-accent" />}
                              </span>
                              <span className="min-w-0">
                                <span className="flex items-center gap-2">
                                  <span className="text-sm font-medium">{p.name}</span>
                                  {PLAN_BADGE[p.plan] && (
                                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-accent/20 text-accent">{PLAN_BADGE[p.plan]}</span>
                                  )}
                                </span>
                                <span className="block text-xs text-foreground-muted mt-0.5" data-testid={`register-plan-tagline-${p.plan}`}>
                                  {planTagline(p.plan, p.max_users, lang)}
                                </span>
                              </span>
                            </span>
                            <span className="text-sm font-semibold whitespace-nowrap mt-0.5">{ISK(p.monthly_isk_inc_vat)} kr/mán</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    {!showInviteCode ? (
                      <button
                        type="button"
                        onClick={() => setShowInviteCode(true)}
                        className="flex items-center gap-1.5 text-xs text-foreground-muted hover:text-accent transition-colors"
                        data-testid="show-invite-code-btn"
                      >
                        <Ticket size={11} />
                        {lang === 'is' ? 'Ertu með boðskóða?' : 'Have an invite code?'}
                      </button>
                    ) : (
                      <div className="space-y-2">
                        <label className="block text-xs text-foreground-muted">
                          <Ticket size={11} className="inline mr-1" />
                          {t('register.invite_code_label')}
                        </label>
                        <input
                          type="text"
                          value={formData.invite_code}
                          onChange={(e) => handleCodeChange(e.target.value)}
                          placeholder={t('register.invite_code_ph')}
                          className="font-mono uppercase"
                          data-testid="register-invite-code"
                        />
                        {codeChecking && (
                          <p className="flex items-center gap-1.5 text-xs text-foreground-muted" data-testid="invite-code-checking">
                            <Loader2 size={12} className="animate-spin" />
                          </p>
                        )}
                        {!codeChecking && codeStatus === true && (
                          <p className="flex items-center gap-1.5 text-xs text-accent" data-testid="invite-code-valid">
                            <Check size={12} />
                            {t('register.invite_code_success')}
                          </p>
                        )}
                        {!codeChecking && codeStatus === false && (
                          <p className="flex items-center gap-1.5 text-xs text-error" data-testid="invite-code-invalid">
                            <X size={12} />
                            {t('register.invite_code_error_invalid')}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer group" data-testid="register-terms-checkbox">
                    <span
                      role="checkbox"
                      aria-checked={agreedToTerms}
                      tabIndex={0}
                      onClick={() => setAgreedToTerms(v => !v)}
                      onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); setAgreedToTerms(v => !v); } }}
                      className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${agreedToTerms ? 'bg-accent border-accent' : 'border-border bg-input group-hover:border-foreground-muted'}`}
                      data-testid="register-terms-checkbox-box"
                    >
                      {agreedToTerms && <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                    </span>
                    <span className="text-sm text-foreground-secondary leading-snug">
                      {t('register.terms_prefix')}{' '}
                      <Link to="/terms" className="text-accent hover:underline">{t('register.terms_link')}</Link>{' '}
                      {t('register.terms_and')}{' '}
                      <Link to="/privacy" className="text-accent hover:underline">{t('register.privacy_link')}</Link>{' '}
                      Rektra
                    </span>
                  </label>
                </>
              )}

              <button type="submit" disabled={isLoading} className="w-full btn btn-primary btn-lg" data-testid="register-submit">
                {isLoading ? <div className="spinner w-5 h-5" /> : (
                  <><UserPlus size={18} />{existingPasswordMode
                    ? (lang === 'is' ? 'Bæta fyrirtækinu við' : 'Add the company')
                    : (lang === 'is' ? 'Byrja frían prufutíma' : t('register.submit'))}</>
                )}
              </button>

              {!existingPasswordMode && (
                <p className="text-xs text-foreground-muted text-center" data-testid="register-trial-hint">
                  {signupNoticeText}
                </p>
              )}

              <p className="text-xs text-foreground-muted text-center" data-testid="register-legal-text">{t('register.legal_text')}</p>

              {!existingPasswordMode && (
                <button type="button" onClick={() => setStep(1)} className="w-full flex items-center justify-center gap-1.5 text-xs text-foreground-muted hover:text-foreground transition-colors" data-testid="register-step-2-back">
                  <ArrowLeft size={12} />{lang === 'is' ? 'Til baka' : 'Back'}
                </button>
              )}
            </form>
          )}

          <p className="mt-8 text-center text-sm text-foreground-muted">
            {t('register.have_account')}{' '}
            <Link to="/login" className="text-accent hover:underline" data-testid="login-link">{t('register.login_link')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
